import React, { useEffect, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { getRoute } from "../lib/geo";
import { useTheme } from "../context/ThemeContext";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const makePointIcon = (bg, size = 18, ring = "#05070B") =>
  L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border:3px solid ${ring};background:${bg};border-radius:9999px;box-shadow:0 0 0 2px ${bg},0 0 18px ${bg};"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

export const pickupIcon = makePointIcon("#00D4FF", 18);
export const destIcon = makePointIcon("#00FF88", 18);
export const stopIcon = makePointIcon("#FFB000", 16);
export const userLocationIcon = makePointIcon("#3B82F6", 20);

const vehicleSvg = (type, color) => {
  if (type === "moto") {
    return `<svg width="26" height="18" viewBox="0 0 52 36" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="25" r="7" stroke="${color}" stroke-width="4"/><circle cx="40" cy="25" r="7" stroke="${color}" stroke-width="4"/><path d="M15 24h8l7-13h8l4 13M24 24l-8-10h-6" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M30 11h-7" stroke="${color}" stroke-width="4" stroke-linecap="round"/></svg>`;
  }
  if (type === "xl") {
    return `<svg width="28" height="18" viewBox="0 0 56 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 20l6-10h28l9 10v8H6v-8Z" stroke="${color}" stroke-width="4" stroke-linejoin="round"/><path d="M17 10v10M30 10v10" stroke="${color}" stroke-width="3"/><circle cx="16" cy="29" r="4" fill="${color}"/><circle cx="43" cy="29" r="4" fill="${color}"/></svg>`;
  }
  return `<svg width="28" height="18" viewBox="0 0 56 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 22l7-10h24l10 10v7H7v-7Z" stroke="${color}" stroke-width="4" stroke-linejoin="round"/><path d="M18 12l-4 10h27l-7-10H18Z" fill="${color}" fill-opacity=".22"/><circle cx="17" cy="29" r="4" fill="${color}"/><circle cx="42" cy="29" r="4" fill="${color}"/></svg>`;
};

const vehicleIcon = (type = "economy", active = false) => {
  const color = {
    moto: "#FFD60A",
    economy: "#00D4FF",
    comfort: "#00FF88",
    premium: "#B77DFF",
    xl: "#FF7A00",
  }[type] || "#00D4FF";
  const size = active ? 42 : 34;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:999px;background:rgba(5,7,11,.88);border:2px solid ${color};box-shadow:0 0 18px ${color};display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px)">${vehicleSvg(type, color)}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const OSM_TILE_LAYER = {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  maxZoom: 20,
  maxNativeZoom: 19,
};

const CARTO_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const TILE_PERFORMANCE_OPTIONS = {
  updateWhenIdle: true,
  updateWhenZooming: false,
  keepBuffer: 2,
  crossOrigin: true,
};

const OVERPASS_URL = process.env.REACT_APP_OVERPASS_URL || "https://overpass-api.de/api/interpreter";
const POI_LABELS_ENABLED = process.env.REACT_APP_MAP_POI_LABELS !== "false";
const POI_LABEL_MIN_ZOOM = Number(process.env.REACT_APP_MAP_POI_MIN_ZOOM || 14);
const POI_LABEL_LIMIT = Number(process.env.REACT_APP_MAP_POI_LABEL_LIMIT || 45);
const POI_QUERY_LIMIT = Math.min(140, Math.max(POI_LABEL_LIMIT, POI_LABEL_LIMIT * 3));
const POI_LABEL_CACHE = new Map();

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const poiRank = (tags = {}) => {
  if (tags.office || tags.shop) return 1;
  if (tags.amenity) return 2;
  if (tags.healthcare || tags.tourism) return 3;
  if (tags.leisure || tags.craft) return 4;
  return 5;
};

const poiKind = (tags = {}) =>
  tags.office ||
  tags.shop ||
  tags.amenity ||
  tags.healthcare ||
  tags.tourism ||
  tags.leisure ||
  tags.craft ||
  tags.building ||
  "lieu";

const poiCategory = (tags = {}) => {
  const amenity = tags.amenity;
  if (tags.office) return "office";
  if (tags.shop) return "shop";
  if (["hospital", "clinic", "pharmacy", "doctors", "dentist"].includes(amenity) || tags.healthcare) return "health";
  if (["school", "college", "university", "kindergarten"].includes(amenity)) return "school";
  if (["restaurant", "cafe", "bar", "fast_food"].includes(amenity)) return "food";
  if (["bank", "atm", "bureau_de_change"].includes(amenity)) return "finance";
  if (tags.tourism) return "tourism";
  if (tags.leisure) return "leisure";
  if (tags.craft) return "service";
  if (tags.building) return "building";
  return "place";
};

const POI_CATEGORY_META = {
  building: {
    label: "Batiment",
    color: "#B77DFF",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M6 21V4h9v17M15 9h4v12M9 8h3M9 12h3M9 16h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  office: {
    label: "Bureau",
    color: "#00D4FF",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M4 21V7l8-4 8 4v14M8 11h2M14 11h2M8 15h2M14 15h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  shop: {
    label: "Commerce",
    color: "#00FF88",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M5 10h14l-1 11H6L5 10ZM8 10a4 4 0 0 1 8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  health: {
    label: "Sante",
    color: "#FF4D6D",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>',
  },
  school: {
    label: "Ecole",
    color: "#FFD60A",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="m3 9 9-5 9 5-9 5-9-5Zm4 4v4c3 2 7 2 10 0v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  food: {
    label: "Resto",
    color: "#FF7A00",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M7 3v8M11 3v8M7 7h4M17 3v18M6 21h12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  finance: {
    label: "Finance",
    color: "#22C55E",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M4 10h16M6 10v8M10 10v8M14 10v8M18 10v8M5 21h14M12 3l8 5H4l8-5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  tourism: {
    label: "Hotel",
    color: "#38BDF8",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M4 21V5h16v16M8 9h3M13 9h3M8 13h3M13 13h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  leisure: {
    label: "Loisir",
    color: "#F97316",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 3v18M3 12h18M5 5l14 14M19 5 5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  },
  service: {
    label: "Service",
    color: "#A3E635",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M14 7 9 12l5 5M4 12h15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
  place: {
    label: "Lieu",
    color: "#94A3B8",
    icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 21s7-5 7-11a7 7 0 1 0-14 0c0 6 7 11 7 11Z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="10" r="2.5" stroke="currentColor" stroke-width="2"/></svg>',
  },
};

const poiLabelIcon = (place) =>
  L.divIcon({
    className: `nexora-poi-label nexora-poi-label-${place.category}`,
    html: `<span style="--poi-color:${escapeHtml(place.color)}"><i>${place.icon}</i><b>${escapeHtml(place.name)}</b></span>`,
    iconSize: [190, 28],
    iconAnchor: [95, 14],
  });

const buildPoiQuery = (bounds) => {
  const bbox = [
    bounds.getSouth().toFixed(5),
    bounds.getWest().toFixed(5),
    bounds.getNorth().toFixed(5),
    bounds.getEast().toFixed(5),
  ].join(",");

  return `
    [out:json][timeout:6];
    (
      node["name"][~"^(amenity|shop|office|tourism|leisure|healthcare|craft|building)$"~"."](${bbox});
      way["name"][~"^(amenity|shop|office|tourism|leisure|healthcare|craft|building)$"~"."](${bbox});
      relation["name"][~"^(amenity|shop|office|tourism|leisure|healthcare|craft|building)$"~"."](${bbox});
    );
    out center tags ${POI_QUERY_LIMIT};
  `;
};

function PoiLabelsLayer({ enabled = true }) {
  const map = useMap();
  const [places, setPlaces] = useState([]);
  const lastKey = useRef("");

  useEffect(() => {
    if (!enabled || !POI_LABELS_ENABLED) {
      setPlaces([]);
      return undefined;
    }

    let timer = null;
    let controller = null;

    const loadPlaces = () => {
      if (map.getZoom() < POI_LABEL_MIN_ZOOM) {
        setPlaces([]);
        return;
      }

      const bounds = map.getBounds();
      const key = [
        map.getZoom(),
        bounds.getSouth().toFixed(3),
        bounds.getWest().toFixed(3),
        bounds.getNorth().toFixed(3),
        bounds.getEast().toFixed(3),
      ].join(":");

      if (lastKey.current === key) return;
      lastKey.current = key;

      const cachedPlaces = POI_LABEL_CACHE.get(key);
      if (cachedPlaces) {
        setPlaces(cachedPlaces);
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();

      fetch(OVERPASS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: `data=${encodeURIComponent(buildPoiQuery(bounds))}`,
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error("Overpass unavailable");
          return response.json();
        })
        .then((data) => {
          const nextPlaces = (data.elements || [])
            .map((item) => {
              const lat = item.lat ?? item.center?.lat;
              const lng = item.lon ?? item.center?.lon;
              const tags = item.tags || {};
              const name = tags.name || tags.brand;
              if (!lat || !lng || !name) return null;
              const category = poiCategory(tags);
              const meta = POI_CATEGORY_META[category] || POI_CATEGORY_META.place;
              return {
                id: `${item.type}-${item.id}`,
                lat,
                lng,
                name,
                category,
                color: meta.color,
                icon: meta.icon,
                label: meta.label,
                kind: poiKind(tags),
                rank: poiRank(tags),
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.rank - b.rank || a.name.length - b.name.length)
            .slice(0, POI_LABEL_LIMIT);
          POI_LABEL_CACHE.set(key, nextPlaces);
          if (POI_LABEL_CACHE.size > 24) {
            const oldestKey = POI_LABEL_CACHE.keys().next().value;
            POI_LABEL_CACHE.delete(oldestKey);
          }
          setPlaces(nextPlaces);
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            lastKey.current = "";
          }
        });
    };

    const scheduleLoad = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(loadPlaces, 450);
    };

    scheduleLoad();
    map.on("moveend zoomend", scheduleLoad);

    return () => {
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
      map.off("moveend zoomend", scheduleLoad);
    };
  }, [enabled, map]);

  return (
    <>
      {places.map((place) => (
        <Marker key={place.id} position={[place.lat, place.lng]} icon={poiLabelIcon(place)} interactive={false}>
          <Popup>
            <strong>{place.name}</strong>
            <div>{place.label} - {place.kind}</div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}

function ClickHandler({ onClick }) {
  const map = useMap();

  useEffect(() => {
    if (!onClick) return undefined;
    const handler = (e) => onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [map, onClick]);

  return null;
}

function MapController({ center, zoom }) {
  const map = useMap();
  const last = useRef(null);

  useEffect(() => {
    const container = map.getContainer();
    const invalidate = () => map.invalidateSize({ animate: false });
    invalidate();
    const timers = [120, 400, 900].map((delay) => setTimeout(invalidate, delay));
    const observer = new ResizeObserver(invalidate);
    observer.observe(container);
    return () => {
      timers.forEach(clearTimeout);
      observer.disconnect();
    };
  }, [map]);

  useEffect(() => {
    if (!center) return;
    const nextZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : map.getZoom();
    const key = `${center.lat},${center.lng},${nextZoom}`;
    if (last.current === key) return;
    last.current = key;
    map.flyTo([center.lat, center.lng], nextZoom, { duration: 0.6 });
  }, [center, map, zoom]);

  return null;
}

function TileLayerWithFallback({ tileTheme, showAttribution }) {
  const [fallback, setFallback] = useState(false);
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
  const tileProvider = (process.env.REACT_APP_MAP_TILE_PROVIDER || "balanced").toLowerCase();
  const isDark = tileTheme === "dark";
  const eventHandlers = {
    tileerror: () => setFallback(true),
  };

  if (fallback) {
    return (
      <TileLayer
        key={`osm-fallback-${isDark ? "dark" : "light"}`}
        {...OSM_TILE_LAYER}
        {...TILE_PERFORMANCE_OPTIONS}
        className={isDark ? "nexora-map-tiles-dark" : "nexora-map-tiles-light"}
        attribution={showAttribution ? OSM_TILE_LAYER.attribution : ""}
        eventHandlers={eventHandlers}
      />
    );
  }

  if (tileProvider === "mapbox" && isDark && mapboxToken) {
    return (
      <TileLayer
        key="mapbox-dark"
        attribution={
          showAttribution
            ? '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            : ""
        }
        url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/512/{z}/{x}/{y}@2x?access_token=${mapboxToken}`}
        tileSize={512}
        zoomOffset={-1}
        maxZoom={20}
        eventHandlers={eventHandlers}
      />
    );
  }

  if (tileProvider === "osm") {
    return (
      <TileLayer
        key={`osm-detailed-${isDark ? "dark" : "light"}`}
        {...OSM_TILE_LAYER}
        {...TILE_PERFORMANCE_OPTIONS}
        className={isDark ? "nexora-map-tiles-dark" : "nexora-map-tiles-light"}
        attribution={showAttribution ? OSM_TILE_LAYER.attribution : ""}
        eventHandlers={eventHandlers}
      />
    );
  }

  if (tileTheme === "light") {
    return (
      <TileLayer
        key="carto-light-balanced"
        attribution={showAttribution ? CARTO_ATTRIBUTION : ""}
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        subdomains="abcd"
        maxZoom={20}
        {...TILE_PERFORMANCE_OPTIONS}
        eventHandlers={eventHandlers}
      />
    );
  }

  return (
    <TileLayer
      key="carto-dark-balanced"
      attribution={showAttribution ? CARTO_ATTRIBUTION : ""}
      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
      subdomains="abcd"
      maxZoom={20}
      {...TILE_PERFORMANCE_OPTIONS}
      eventHandlers={eventHandlers}
    />
  );
}

export default function MapView({
  center = { lat: -2.8799, lng: 23.656 },
  zoom = 5,
  pickup,
  destination,
  extraStop,
  driver,
  userLocation,
  nearbyDrivers = [],
  driverVehicleType,
  onMapClick,
  height = "100%",
  className = "",
  useRouting = true,
  tileTheme = "auto",
  routeColor = "#00D4FF",
  routeDashArray = "8,6",
  showAttribution = true,
  showPoiLabels = true,
  minHeight = 260,
  scrollWheelZoom = false,
}) {
  const { theme } = useTheme();
  const effectiveTileTheme = tileTheme === "auto" ? theme : tileTheme || theme;
  const [routeCoords, setRouteCoords] = useState(null);
  const pickupLat = pickup?.lat;
  const pickupLng = pickup?.lng;
  const destinationLat = destination?.lat;
  const destinationLng = destination?.lng;
  const extraStopLat = extraStop?.lat;
  const extraStopLng = extraStop?.lng;

  useEffect(() => {
    if (!useRouting || !pickup || !destination) {
      setRouteCoords(null);
      return undefined;
    }

    let alive = true;
    const routePromise = extraStop
      ? Promise.all([getRoute(pickup, extraStop), getRoute(extraStop, destination)]).then(([first, second]) => {
          if (!first || !second) return null;
          return { coordinates: [...first.coordinates, ...second.coordinates.slice(1)] };
        })
      : getRoute(pickup, destination);
    routePromise.then((route) => {
      if (alive && route) setRouteCoords(route.coordinates);
    });

    return () => {
      alive = false;
    };
  }, [useRouting, pickup, destination, extraStop, pickupLat, pickupLng, destinationLat, destinationLng, extraStopLat, extraStopLng]);

  return (
    <div className={`relative ${className}`} style={{ height, minHeight, width: "100%" }}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{ height: "100%", minHeight, width: "100%" }}
        zoomControl={false}
        scrollWheelZoom={scrollWheelZoom}
        preferCanvas
      >
        <TileLayerWithFallback tileTheme={effectiveTileTheme} showAttribution={showAttribution} />
        <ClickHandler onClick={onMapClick} />
        <MapController center={center} zoom={zoom} />
        <PoiLabelsLayer enabled={showPoiLabels} />

        {pickup && (
          <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon}>
            <Popup>Point de prise en charge</Popup>
          </Marker>
        )}

        {destination && (
          <Marker position={[destination.lat, destination.lng]} icon={destIcon}>
            <Popup>Destination</Popup>
          </Marker>
        )}

        {extraStop && (
          <Marker position={[extraStop.lat, extraStop.lng]} icon={stopIcon}>
            <Popup>Arret supplementaire</Popup>
          </Marker>
        )}

        {driver && (
          <Marker position={[driver.lat, driver.lng]} icon={vehicleIcon(driverVehicleType || driver.vehicle_type || "economy", true)}>
            <Popup>Chauffeur</Popup>
          </Marker>
        )}

        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
            <Popup>Votre position</Popup>
          </Marker>
        )}

        {nearbyDrivers.map((item) => (
          <Marker
            key={item.driver_id || `${item.lat},${item.lng},${item.vehicle_type}`}
            position={[item.lat, item.lng]}
            icon={vehicleIcon(item.vehicle_type)}
          >
            <Popup>
              <div style={{ minWidth: 140 }}>
                <strong>{item.name || "Chauffeur"}</strong>
                <div>{item.vehicle_type || "vehicule"} {item.vehicle_model ? `- ${item.vehicle_model}` : ""}</div>
                <div>{item.distance_km} km autour de vous</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {routeCoords ? (
          <Polyline
            positions={routeCoords}
            pathOptions={{ color: routeColor, weight: 5, opacity: 0.9 }}
          />
        ) : (
          pickup &&
          destination && (
            <Polyline
              positions={[
                [pickup.lat, pickup.lng],
                ...(extraStop ? [[extraStop.lat, extraStop.lng]] : []),
                [destination.lat, destination.lng],
              ]}
              pathOptions={{ color: routeColor, weight: 4, dashArray: routeDashArray, opacity: 0.8 }}
            />
          )
        )}
      </MapContainer>

      {!showAttribution && <style>{`.leaflet-control-attribution{display:none!important}`}</style>}
    </div>
  );
}
